import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactElement,
} from "react";

import { Box } from "../../components/ui/box";
import { Button, ButtonExpansion } from "../../components/ui/button";
import { Icon, type IconName } from "../../components/ui/icon";
import { Tabs, TabsList, TabsTab } from "../../components/ui/tabs";
import { Text } from "../../components/ui/text";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import type { DataTableGroupSortDirection } from "../../hooks/use-data-table-state";
import { useIsMobile } from "../../hooks/use-media-query";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";
import {
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorFooter,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  useSelectorKeyboardNav,
  type EntityItemConfig,
  type EntitySelection,
  type EntitySelectorConfig,
} from "../entity-selector";

/** The cleared field key. Hosts carry it in their state; the field list never
 *  offers it as a field — clearing is the clear row's job. */
export const NONE_FIELD = "none";

/**
 * The clear row's accessible name. What the eye reads is the em dash every
 * empty value wears; a bare dash announces as punctuation where the row's
 * whole job is to name the absence of a value, so the word stays the row's
 * name for search and assistive tech.
 */
const NONE_LABEL = "None";
const CLEAR_ROW_MARK = "—";

/** The one pool: the fields. */
const POOL_ID = "fields";

/** Prefixed so it can never collide with a host's field key. */
const CLEAR_ROW_ID = "field-dropdown:clear";

/**
 * A list this long earns a search well; a shorter one is scanned, and a well
 * above four rows costs a row of height and lights a focus ring for a field
 * nobody types in. Without the well the popup itself holds focus and hears
 * the arrows and Enter.
 */
const SEARCH_THRESHOLD = 8;

/** The split chip's seam: the arrow half's own leading hairline, in the
 *  chip's border ink, so the ring stays one continuous stadium. */
const SPLIT_LEAD_CLASS =
  "rounded-e-none border-e-0 before:rounded-e-none";
const SPLIT_TRAIL_CLASS =
  "rounded-s-none before:rounded-s-none";

const DIRECTION_LABEL: Record<DataTableGroupSortDirection, string> = {
  asc: "Ascending",
  desc: "Descending",
};

const OTHER_DIRECTION: Record<
  DataTableGroupSortDirection,
  DataTableGroupSortDirection
> = { asc: "desc", desc: "asc" };

/** The words that differ between the dropdown's flavors — "Group by" vs
 *  "Sort by" — so the field-list popover mechanics live here once. */
export type FieldDropdownCopy = {
  /** The compact trigger's accessible name and the field list's. */
  title: string;
  /** Tooltip lead-in: "Grouping by" / "Sorting by". */
  tooltipVerb: string;
  /** The direction control's accessible name: "Group order" / "Sort order". */
  directionLabel: string;
  /** The compact trigger's glyph. */
  icon: IconName;
};

export type DataTableDirectionIconProps = Omittable<{
  direction: DataTableGroupSortDirection;
  size?: ComponentProps<typeof Icon>["size"];
  color?: ComponentProps<typeof Icon>["color"];
  /** Name the direction for assistive tech. Omit where a neighbouring label
   *  or control already states it — the glyph is then decoration. */
  labelled?: boolean;
}>;

/**
 * The direction as a glyph: one arrow that points up for ascending and turns
 * to point down for descending. It is ONE arrow rather than two icons so a
 * flip reads as the arrow turning over, not as one symbol swapped for another.
 * Wherever a host states a field it can state the field's direction beside it
 * with this — the toolbar chip's arrow half, a settings row's value readout.
 */
export function DataTableDirectionIcon({
  direction,
  size = "xs",
  color,
  labelled = false,
}: DataTableDirectionIconProps): ReactElement {
  return (
    <Icon
      aria-label={labelled ? DIRECTION_LABEL[direction] : undefined}
      className={cn(
        "transition-transform duration-base ease-out motion-reduce:transition-none",
        direction === "desc" && "rotate-180",
      )}
      color={color}
      name="arrow-up"
      size={size}
    />
  );
}

export type FieldDirectionDropdownProps = {
  field: string;
  setField: (field: string) => void;
  labels: Record<string, string>;
  icons?: Partial<Record<string, IconName>> | undefined;
  direction?: DataTableGroupSortDirection | undefined;
  setDirection?: ((direction: DataTableGroupSortDirection) => void) | undefined;
  /** The host cannot render cleared — the clear row is not offered at all. */
  requireField?: boolean | undefined;
  trigger?: ReactElement<Record<string, unknown>> | undefined;
  iconOnly?: boolean | undefined;
  copy: FieldDropdownCopy;
};

/**
 * The shared internals of DataTableGroupByDropdown / DataTableSortByDropdown.
 *
 * The popover IS the field list — the selector rung every picker in the
 * library opens at: one row per field with its glyph on the rail and a check
 * on the active one, the "—" clear row heading the list, a search well once
 * the list is long enough to need one, and a footer holding the direction as
 * a two-way toggle. Picking a row commits and closes; the direction flips in
 * place and leaves the list open.
 *
 * The compact toolbar chip is split while a field is set: its label half
 * opens the list, its arrow half reads the direction and reverses it on
 * press — no popup for the one-bit change. A caller-owned trigger and the
 * collapsed (icon-only / phone) face carry no arrow half; there the footer
 * toggle is the direction control. On a phone the list opens as a bottom
 * drawer, the way every picker does there: a popover hung off a 24px chip
 * has no room to be a list of thumb-sized rows, and the drawer's pool label
 * takes over naming the list from the chip.
 *
 * Internal — not in the barrel; the named flavors are the public surface.
 */
export function FieldDirectionDropdown({
  field,
  setField,
  labels,
  icons = {},
  direction = "asc",
  setDirection,
  trigger,
  iconOnly = false,
  requireField = false,
  copy,
}: FieldDirectionDropdownProps) {
  // A phone's toolbar holds its chips at one footprint: the field's name
  // never slides open there, so the row cannot wrap and push its tail off
  // screen. The tint carries the "set" state and the tooltip names the field.
  const isMobile = useIsMobile();
  const collapsed = iconOnly || isMobile;
  const [localField, setLocalField] = useState(field);
  const [localDirection, setLocalDirection] =
    useState<DataTableGroupSortDirection>(direction);

  useEffect(() => {
    setLocalField(field);
  }, [field]);
  useEffect(() => {
    setLocalDirection(direction);
  }, [direction]);

  // The picker owns the query so the clear row can be withheld while a search
  // narrows the list — clearing is an act over the whole list, not one of the
  // things being searched for.
  const [search, setSearch] = useState("");
  const { ref, inputProps } = useSelectorKeyboardNav();
  const query = search.trim();
  const popupRef = useRef<HTMLDivElement>(null);
  // Focus the popup itself when there is no field to take it: the keys then
  // reach the list, and the open does not land a ring on the footer's first
  // tab.
  const focusPopup = useCallback(() => popupRef.current ?? true, []);

  const hasField = localField !== NONE_FIELD;

  const emitFieldChange = useCallback(
    (key: string) => {
      setLocalField(key);
      setField(key);
    },
    [setField],
  );

  const emitDirectionChange = useCallback(
    (dir: DataTableGroupSortDirection) => {
      setLocalDirection(dir);
      setDirection?.(dir);
    },
    [setDirection],
  );

  const flipDirection = useCallback(() => {
    emitDirectionChange(OTHER_DIRECTION[localDirection]);
  }, [emitDirectionChange, localDirection]);

  // Clearing is a row of its own, so the fields are fields and nothing else.
  const fieldOptions = useMemo(
    () =>
      Object.entries(labels)
        .filter(([key]) => key !== NONE_FIELD)
        .map(([key, label]) => ({ value: key, label })),
    [labels],
  );

  const searchable = fieldOptions.length >= SEARCH_THRESHOLD;

  // The clear row is offered wherever the surface can render cleared; a
  // `requireField` surface omits it outright — there it could never act.
  const canClear = !requireField;

  // A list where some fields carry a glyph reserves the visual rail on EVERY
  // row, so labels stay on one reading edge; a list with none has no rail.
  const hasIcons = useMemo(
    () => fieldOptions.some(({ value }) => icons[value]),
    [fieldOptions, icons],
  );

  const items = useMemo<EntityItemConfig[]>(() => {
    const rows: EntityItemConfig[] = [];
    if (canClear && !query) {
      rows.push({
        actionRow: true,
        // The dash sits where the other rows' glyphs sit, so it reads as the
        // rail's own "nothing" rather than as a word.
        body: () =>
          hasIcons ? (
            <Box
              align="center"
              direction="row"
              height={24}
              justify="center"
              shrink={false}
              width={24}
            >
              <Text size="sm" variant="muted">
                {CLEAR_ROW_MARK}
              </Text>
            </Box>
          ) : (
            <Text size="sm" variant="muted">
              {CLEAR_ROW_MARK}
            </Text>
          ),
        id: CLEAR_ROW_ID,
        title: NONE_LABEL,
      });
    }
    for (const { value, label } of fieldOptions) {
      const iconName = icons[value];
      rows.push({
        id: value,
        title: label,
        ...(hasIcons
          ? {
              avatar: iconName
                ? // Un-padded and image-less: the row draws the bare glyph
                  // on its 24px rail in muted ink.
                  { icon: iconName }
                : // A field with no glyph still holds the rail open.
                  {
                    render: () => <Box height={24} shrink={false} width={24} />,
                  },
            }
          : {}),
      });
    }
    return rows;
  }, [canClear, fieldOptions, hasIcons, icons, query]);

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      emptyMessage: "No matching fields",
      // Field names are a vocabulary, not named things: the entity weight
      // would read the list as a roster of proper nouns.
      itemTitleWeight: "normal",
      maxSelections: 1,
      pools: [
        {
          hideAvatars: !hasIcons,
          id: POOL_ID,
          items,
          label: copy.title,
        },
      ],
      searchPlaceholder: "Search fields",
    }),
    [copy.title, hasIcons, items],
  );

  // The trigger's own face only names the field while one is set — the
  // tooltip always answers the question, "None" included.
  const activeLabel = hasField ? (labels[localField] ?? localField) : NONE_LABEL;

  // The applied field lives with the host, so the selector holds a mirror of
  // it rather than a selection of its own — which is what puts the check on
  // the current row and homes the cursor there when the list opens.
  const selections = useMemo<EntitySelection[]>(
    () =>
      hasField
        ? [
            {
              entity: { id: localField, title: activeLabel },
              id: localField,
              poolId: POOL_ID,
            },
          ]
        : [],
    [activeLabel, hasField, localField],
  );

  const handleBeforeSelect = useCallback(
    (entity: EntityItemConfig) => {
      emitFieldChange(entity.id === CLEAR_ROW_ID ? NONE_FIELD : entity.id);
      // Every press is vetoed: the selector never accumulates a selection of
      // its own — the host's `field` is the single source of what is applied
      // — and the veto is what closes the list, a re-pick of the current row
      // included.
      return false;
    },
    [emitFieldChange],
  );

  // The arrow half rides the expanded chip only: a collapsed face has no
  // room for a second hit zone, and a field-less chip has nothing to direct.
  const splitChip = hasField && !collapsed && !!setDirection;

  const face = (
    <Button
      color={hasField ? "primary" : "neutral"}
      variant={hasField ? "outline" : "ghost"}
      size="xs"
      expanded={hasField && !collapsed}
      // Icon-only mode has no visible text to name the control.
      aria-label={hasField && !collapsed ? undefined : copy.title}
      className={splitChip ? SPLIT_LEAD_CLASS : undefined}
    >
      <Icon name={copy.icon} size="sm" />
      {/* Set fields only: the "none" entry must never paint, even for
          the collapse animation's held frame — ButtonExpansion holds the
          last non-null children while the slot slides shut. */}
      <ButtonExpansion>{hasField ? labels[localField] : null}</ButtonExpansion>
    </Button>
  );

  const buttonTrigger = (
    <Tooltip>
      <EntitySelectorTrigger render={<TooltipTrigger render={face} />} />
      <TooltipPopup side="top">{`${copy.tooltipVerb}: ${activeLabel}`}</TooltipPopup>
    </Tooltip>
  );

  // Two halves of ONE stadium: the label half drops its trailing edge and
  // radius, the arrow half its leading radius, and the arrow half's own
  // leading border is the seam — one hairline in the chip's border ink, no
  // separator column to notch the ring at top and bottom.
  const chip = splitChip ? (
    <Box align="center" direction="row" shrink={false}>
      {buttonTrigger}
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              aria-label={`${copy.directionLabel}: ${DIRECTION_LABEL[localDirection]}`}
              className={SPLIT_TRAIL_CLASS}
              onClick={flipDirection}
              size="xs"
              color="primary"
              variant="outline"
            />
          }
        >
          <DataTableDirectionIcon direction={localDirection} size="sm" />
        </TooltipTrigger>
        <TooltipPopup side="top">
          {`Switch to ${DIRECTION_LABEL[OTHER_DIRECTION[localDirection]].toLowerCase()}`}
        </TooltipPopup>
      </Tooltip>
    </Box>
  ) : (
    buttonTrigger
  );

  return (
    <EntitySelector
      config={config}
      onBeforeSelect={handleBeforeSelect}
      onSearchChange={setSearch}
      ref={ref}
      search={search}
      title={copy.title}
      value={selections}
    >
      {trigger ? <EntitySelectorTrigger render={trigger} /> : chip}
      <EntitySelectorContent
        align="start"
        // Without a search well the popup is the keyboard's seat: it takes
        // focus on open and hears the arrows and Enter for the list.
        initialFocus={searchable ? undefined : focusPopup}
        onKeyDown={searchable ? undefined : inputProps.onKeyDown}
        ref={popupRef}
      >
        {searchable ? <EntitySelectorInput {...inputProps} /> : null}
        {/* The pool's label is kept for the listbox's accessible name only:
            anchored to the chip, the chip already says what the rows are;
            in the drawer the sheet's caption (`title` above) does. */}
        <EntitySelectorPool hideLabel value={POOL_ID} />
        {setDirection ? (
          <EntitySelectorFooter>
            {/* Two ways to order is a switch, not a list: both states are on
                screen and one press moves between them. On the default rung —
                the rows' 14px line, thumb-sized on a phone — inside the
                footer's 6px frame, so it reads as a control the list closes
                on rather than a strip tucked under it. Inert while no field
                is set — nothing to order — but still standing, so the popup
                is the same object open after open. */}
            <Tabs
              onValueChange={(value) =>
                emitDirectionChange(value as DataTableGroupSortDirection)
              }
              value={localDirection}
            >
              <TabsList aria-label={copy.directionLabel} fullWidth>
                <TabsTab disabled={!hasField} value="asc">
                  <Icon name="arrow-up" size="xs" />
                  {DIRECTION_LABEL.asc}
                </TabsTab>
                <TabsTab disabled={!hasField} value="desc">
                  <Icon name="arrow-down" size="xs" />
                  {DIRECTION_LABEL.desc}
                </TabsTab>
              </TabsList>
            </Tabs>
          </EntitySelectorFooter>
        ) : null}
      </EntitySelectorContent>
    </EntitySelector>
  );
}
